import React from "react";
import { motion, useReducedMotion, Variants } from "framer-motion";

interface RevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  variant?: "up" | "fade" | "left" | "right" | "scale";
  as?: "div" | "span";
}

const buildVariants = (variant: RevealProps["variant"]): Variants => {
  switch (variant) {
    case "fade":
      return { hidden: { opacity: 0 }, visible: { opacity: 1 } };
    case "left":
      return { hidden: { opacity: 0, x: -32 }, visible: { opacity: 1, x: 0 } };
    case "right":
      return { hidden: { opacity: 0, x: 32 }, visible: { opacity: 1, x: 0 } };
    case "scale":
      return { hidden: { opacity: 0, scale: 0.92 }, visible: { opacity: 1, scale: 1 } };
    default:
      return { hidden: { opacity: 0, y: 36 }, visible: { opacity: 1, y: 0 } };
  }
};

export const Reveal: React.FC<RevealProps> = ({
  children,
  className,
  delay = 0,
  variant = "up",
}) => {
  const shouldReduceMotion = useReducedMotion();
  const variants = buildVariants(variant);

  if (shouldReduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.25 }}
      variants={variants}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};
